/**
 * Desktop Notifications Service
 * Handles Web Notifications API for browser push notifications
 */

import { isSecondaryAppWindow } from '@/lib/desktopWindow';

export interface DesktopNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  requireInteraction?: boolean;
  silent?: boolean;
  /** Also from a secondary window (a call popped out into its own window — TRUS-668). */
  anyWindow?: boolean;
}

export type NotificationPermissionState = 'granted' | 'denied' | 'default';

// The Tauri desktop runtime exposes window.__TAURI__ (withGlobalTauri: true).
// In the Tauri WKWebView the Web Notifications API is blocked ("denied"), so we
// route through the native @tauri-apps/plugin-notification instead.
const isTauri = (): boolean =>
  typeof window !== 'undefined' && '__TAURI__' in window;

// Messaging pop-outs (extra desktop windows / browser tabs) run this same
// module. Only the main window notifies, or every new message would raise one
// banner per open window.
const isMainWindow = (): boolean => !isSecondaryAppWindow();

class DesktopNotificationService {
  private isSupported: boolean;
  // getPermissionState() is synchronous but the Tauri permission check is async,
  // so we cache the last-known value (primed on construction, refreshed on
  // request/show).
  private tauriPermission: NotificationPermissionState = 'default';

  constructor() {
    this.isSupported =
      typeof window !== 'undefined' && ('Notification' in window || isTauri());
    if (isTauri() && isMainWindow()) {
      void this.refreshTauriPermission();
    }
  }

  private async refreshTauriPermission(): Promise<NotificationPermissionState> {
    try {
      const { isPermissionGranted } = await import('@tauri-apps/plugin-notification');
      this.tauriPermission = (await isPermissionGranted()) ? 'granted' : 'default';
    } catch (error) {
      console.error('Tauri notification permission check failed:', error);
    }
    return this.tauriPermission;
  }

  /**
   * Check if desktop notifications are supported in this environment
   */
  isNotificationSupported(): boolean {
    return this.isSupported;
  }

  /**
   * On the Tauri desktop app, trigger the native macOS permission prompt on first
   * launch (when permission is still 'default'). No-op in the browser, and a no-op
   * once the user has granted or denied (macOS won't re-prompt). This is what makes
   * the OS notification banner actually appear and connects the app to macOS
   * Notification Center.
   */
  async ensureDesktopPermissionPrompt(): Promise<void> {
    if (!isTauri() || !isMainWindow()) return;
    try {
      const { isPermissionGranted, requestPermission } = await import(
        '@tauri-apps/plugin-notification'
      );
      if (await isPermissionGranted()) {
        this.tauriPermission = 'granted';
        return;
      }
      const result = await requestPermission();
      this.tauriPermission = result === 'granted' ? 'granted' : 'denied';
    } catch (error) {
      console.error('Desktop notification permission prompt failed:', error);
    }
  }

  /**
   * Get current notification permission state.
   * On Tauri this returns the cached value; the live state is resolved by
   * requestPermission()/showNotification().
   */
  getPermissionState(): NotificationPermissionState {
    if (isTauri()) return this.tauriPermission;
    if (!('Notification' in window)) return 'denied';
    return Notification.permission;
  }

  /**
   * Request notification permission from the user
   * Returns the new permission state
   */
  async requestPermission(): Promise<NotificationPermissionState> {
    if (isTauri()) {
      try {
        const { isPermissionGranted, requestPermission } = await import(
          '@tauri-apps/plugin-notification'
        );
        if (await isPermissionGranted()) {
          this.tauriPermission = 'granted';
          return 'granted';
        }
        const result = await requestPermission();
        this.tauriPermission = result === 'granted' ? 'granted' : 'denied';
        return this.tauriPermission;
      } catch (error) {
        console.error('Error requesting Tauri notification permission:', error);
        return 'denied';
      }
    }

    if (!this.isSupported) {
      console.warn('Desktop notifications are not supported in this browser');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Show a desktop notification
   * Automatically requests permission if not already granted
   */
  async showNotification(options: DesktopNotificationOptions): Promise<Notification | null> {
    if (!isMainWindow() && !options.anyWindow) return null;
    if (isTauri()) {
      try {
        const { sendNotification } = await import('@tauri-apps/plugin-notification');
        const permission = await this.requestPermission();
        if (permission !== 'granted') {
          console.warn('Notification permission not granted');
          return null;
        }
        sendNotification({
          title: options.title,
          body: options.body,
          icon: options.icon,
        });
      } catch (error) {
        console.error('Error showing Tauri notification:', error);
      }
      // Native notifications don't return a Web Notification object; click
      // handling (showNotificationWithAction) is a no-op on desktop for now.
      return null;
    }

    if (!this.isSupported) {
      console.warn('Desktop notifications are not supported');
      return null;
    }

    // Request permission if needed
    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/logo192.png',
        badge: options.badge || '/logo192.png',
        tag: options.tag,
        data: options.data,
        requireInteraction: options.requireInteraction ?? false,
        silent: options.silent ?? false,
      });

      // Auto-close after 5 seconds unless requireInteraction is true
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      return notification;
    } catch (error) {
      console.error('Error showing desktop notification:', error);
      return null;
    }
  }

  /**
   * Show a notification with a click handler
   */
  async showNotificationWithAction(
    options: DesktopNotificationOptions,
    onClick?: (event: Event) => void,
    onClose?: (event: Event) => void
  ): Promise<Notification | null> {
    const notification = await this.showNotification(options);

    if (notification) {
      if (onClick) {
        notification.onclick = (event) => {
          onClick(event);
          notification.close();
        };
      }

      if (onClose) {
        notification.onclose = onClose;
      }
    }

    return notification;
  }

  /**
   * Show notification for workflow completion
   */
  async notifyWorkflowCompletion(workflowName: string, memberName: string, link?: string) {
    return this.showNotificationWithAction(
      {
        title: 'Workflow Completed',
        body: `${workflowName} has been completed for ${memberName}`,
        tag: 'workflow-completion',
        requireInteraction: true,
        data: { type: 'workflow', link },
      },
      (event) => {
        if (link) {
          window.focus();
          window.location.href = link;
        }
      }
    );
  }

  /**
   * Show notification for inbound communication
   */
  async notifyInboundCommunication(
    from: string,
    preview: string,
    communicationType: 'email' | 'sms' | 'phone',
    link?: string
  ) {
    const typeLabel = {
      email: 'Email',
      sms: 'SMS',
      phone: 'Call',
    }[communicationType];

    return this.showNotificationWithAction(
      {
        title: `New ${typeLabel} from ${from}`,
        body: preview,
        tag: `communication-${communicationType}`,
        requireInteraction: true,
        data: { type: 'communication', communicationType, link },
      },
      (event) => {
        if (link) {
          window.focus();
          window.location.href = link;
        }
      }
    );
  }

  /**
   * Show notification for task assignment
   */
  async notifyTaskAssignment(taskTitle: string, assignedBy: string, dueDate?: string, link?: string) {
    const dueDateText = dueDate ? ` (Due: ${dueDate})` : '';

    return this.showNotificationWithAction(
      {
        title: 'New Task Assigned',
        body: `${assignedBy} assigned you: ${taskTitle}${dueDateText}`,
        tag: 'task-assignment',
        requireInteraction: true,
        data: { type: 'task', link },
      },
      (event) => {
        if (link) {
          window.focus();
          window.location.href = link;
        }
      }
    );
  }

  /**
   * Show notification for urgent/high priority items
   */
  async notifyUrgent(title: string, message: string, link?: string) {
    return this.showNotificationWithAction(
      {
        title: `⚠️ ${title}`,
        body: message,
        tag: 'urgent',
        requireInteraction: true,
        data: { type: 'urgent', link },
      },
      (event) => {
        if (link) {
          window.focus();
          window.location.href = link;
        }
      }
    );
  }

  /**
   * Clear all notifications with a specific tag
   */
  clearNotificationsByTag(tag: string) {
    // Note: This API is not widely supported yet
    // Notifications are typically managed by the browser
    console.log(`Clearing notifications with tag: ${tag}`);
  }
}

// Export singleton instance
export const desktopNotifications = new DesktopNotificationService();
